import React, { useState } from "react";
import { Card, Badge, Space, Typography, Button, Tooltip, Tag } from "antd";
import { motion } from "framer-motion";
import moment from "moment-timezone";
import {
  InfoCircleOutlined,
  MailOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  UserOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import styled from "styled-components";
import ScheduledCampaignDetailModal from "./ScheduledCampaignDetailModal";

// Import CSS (Create this file)
import "@assets/css/ScheduledCampaignCard.css";

const { Text } = Typography;

// Styled Components
const StyledCard = styled(Card)`
  background: linear-gradient(135deg, #722ed1 0%, #531dab 100%);
  border-radius: 16px;
  border: none;
  height: 100%;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(114, 46, 209, 0.15);

  .ant-card-body {
    padding: 20px;
  }

  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 20px rgba(114, 46, 209, 0.2);
  }
`;

const InfoCard = styled(Card)`
  background: rgba(255, 255, 255, 0.15);
  border: none;
  border-radius: 12px;
  backdrop-filter: blur(10px);
  margin-top: 16px;

  .ant-card-body {
    padding: 12px;
  }
`;

const ActionButton = styled(Button)`
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  font-weight: 500;
  height: 40px;
  margin-top: 16px;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    border-color: rgba(255, 255, 255, 0.4);
    color: white;
  }

  &:active {
    background: rgba(255, 255, 255, 0.25);
    color: white;
  }
`;

// Add sched-card class to parent container for CSS namespace

const ScheduledCampaignCard = ({ campaign }) => {
  const [detailVisible, setDetailVisible] = useState(false);

  if (!campaign) return null;

  // Extract campaign information
  const { name, scheduledFor, emailConfiguration, timing } = campaign;

  // Format time until start
  const timeUntilStart = moment(scheduledFor).fromNow();

  // Get total emails if available
  const totalEmails = emailConfiguration?.totalEmails || 0;

  // Get sending rate if available
  const sendingRate = emailConfiguration?.sendingRate?.emailsPerHour || 0;

  // Get total senders if available
  const totalSenders =
    emailConfiguration?.distributionStrategy?.totalSenders || 0;

  // Format estimate completion time
  const estimatedCompletion = timing?.estimatedCompletionTime
    ? moment(timing.estimatedCompletionTime).format("MMM DD, YYYY HH:mm")
    : "Not available";

  // Format scheduled time
  const scheduledTime = moment(scheduledFor).format("MMM DD, YYYY HH:mm");

  return (
    <>
      <StyledCard className="sched-card">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="sched-hover-lift"
        >
          {/* Campaign Header */}
          <Space
            align="center"
            style={{
              marginBottom: 16,
              width: "100%",
              justifyContent: "space-between",
            }}
          >
            <Space>
              <MailOutlined style={{ color: "white", fontSize: 18 }} />
              <Text style={{ color: "white", fontSize: 16, fontWeight: 600 }}>
                {name}
              </Text>
            </Space>
            <Badge
              status="default"
              text={<Text style={{ color: "white" }}>Scheduled</Text>}
            />
          </Space>

          {/* Scheduled Information */}
          <div style={{ marginBottom: 16 }}>
            <Space direction="vertical" style={{ width: "100%" }} size="small">
              <Space>
                <CalendarOutlined style={{ color: "white" }} />
                <Text style={{ color: "white" }}>
                  Scheduled: {scheduledTime}
                </Text>
              </Space>
              <Space>
                <ClockCircleOutlined style={{ color: "white" }} />
                <Text style={{ color: "white" }}>
                  Time until start: {timeUntilStart}
                </Text>
              </Space>
              <Space>
                <MailOutlined style={{ color: "white" }} />
                <Text style={{ color: "white" }}>
                  Recipients: {totalEmails}
                </Text>
              </Space>
              <Space>
                <ThunderboltOutlined style={{ color: "white" }} />
                <Text style={{ color: "white" }}>Rate: {sendingRate}/hour</Text>
              </Space>
              <Space>
                <UserOutlined style={{ color: "white" }} />
                <Text style={{ color: "white" }}>Senders: {totalSenders}</Text>
              </Space>
            </Space>
          </div>

          {/* Estimated Completion */}
          <div style={{ marginTop: 12, textAlign: "center" }}>
            <Tooltip title="Estimated time when all emails will be sent">
              <Tag color="white" style={{ color: "#531DAB", fontWeight: 500 }}>
                Est. completion: {estimatedCompletion}
              </Tag>
            </Tooltip>
          </div>

          {/* Sender Information Card (If available) */}
          {emailConfiguration?.senders &&
            emailConfiguration.senders.length > 0 && (
              <InfoCard>
                <Space
                  direction="vertical"
                  style={{ width: "100%" }}
                  size="small"
                >
                  <Text style={{ color: "rgba(255,255,255,0.85)" }}>
                    Distribution (
                    {emailConfiguration?.distributionStrategy
                      ?.emailsPerSender || 0}{" "}
                    emails/sender):
                  </Text>
                  {emailConfiguration.senders.slice(0, 3).map((sender, idx) => (
                    <div key={idx}>
                      <Text style={{ color: "#fff" }}>
                        •{" "}
                        {sender.email
                          ? sender.email.split("@")[0]
                          : `Sender ${idx + 1}`}
                        {sender.useAlias &&
                          sender.alias &&
                          ` (${sender.alias})`}
                        {sender.useSharedInbox && " (Shared)"}
                      </Text>
                    </div>
                  ))}
                  {emailConfiguration.senders.length > 3 && (
                    <Text style={{ color: "rgba(255,255,255,0.85)" }}>
                      + {emailConfiguration.senders.length - 3} more senders
                    </Text>
                  )}
                </Space>
              </InfoCard>
            )}

          {/* View Details Button */}
          <ActionButton
            icon={<InfoCircleOutlined />}
            onClick={() => setDetailVisible(true)}
            block
          >
            View Campaign Details
          </ActionButton>
        </motion.div>
      </StyledCard>

      <ScheduledCampaignDetailModal
        visible={detailVisible}
        campaign={campaign}
        onClose={() => setDetailVisible(false)}
      />
    </>
  );
};

export default ScheduledCampaignCard;
